/**
 * theme.ts — lys/mørk tema (Carbon White / Gray 100)
 *
 * Mørkt tema = klassen cds--g100 (Carbon Gray 100) på <html>. Carbons soneklasse
 * definerer alle --cds-*-fargetokens og color-scheme, så temabytte krever ingen
 * egen CSS; «dark» settes i tillegg for Tailwind/beUI. Lyst tema = ingen klasse
 * (uendret fra før — tokens kommer fra src/styles/carbon-tokens.css).
 *
 * Valget («light» | «dark» | «system») lagres i localStorage. «system» følger
 * OS-innstillingen (prefers-color-scheme) og er standard til brukeren velger.
 *
 * Første tegning: THEME_INIT_SCRIPT kjører i <head> (layout.tsx) og setter
 * klassene før siden males, så det ikke blinker. Holdes lik applyTheme under.
 */

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'nfiles-theme';

/** Varighet på fargeovergangen ved temabytte (ms) — må samsvare med globals.css. */
const THEME_TRANSITION_MS = 250;

/** Inline-script for <head>. Må gjøre det samme som applyTheme (uten overgang). */
export const THEME_INIT_SCRIPT = `(function(){try{var s=localStorage.getItem('${THEME_STORAGE_KEY}');var d=s==='dark'||(s!=='light'&&window.matchMedia('(prefers-color-scheme: dark)').matches);var c=document.documentElement.classList;c.remove('cds--g100','dark');if(d)c.add('cds--g100','dark')}catch(e){}})()`;

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function readThemeChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

const systemPrefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches;

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : choice;
}

/** Setter temaklassene på <html>. */
function applyTheme(resolved: ResolvedTheme) {
  const classes = document.documentElement.classList;
  classes.remove('cds--g100', 'dark');
  if (resolved === 'dark') classes.add('cds--g100', 'dark');
}

let transitionTimer: number | undefined;

/** Bytter tema med kort fargeovergang (ikke ved oppstart, ikke ved system-endring). */
export function setThemeChoice(choice: ThemeChoice) {
  try {
    if (choice === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY);
    else window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // lagring er valgfritt
  }
  const root = document.documentElement;
  root.classList.add('theme-transition');
  window.clearTimeout(transitionTimer);
  transitionTimer = window.setTimeout(() => root.classList.remove('theme-transition'), THEME_TRANSITION_MS + 50);
  applyTheme(resolveTheme(choice));
  emit();
}

/** For useSyncExternalStore: lytter på endringer fra denne fanen, andre faner og OS. */
export function subscribeTheme(onChange: () => void) {
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const onSystemChange = () => {
    // Følger OS bare når brukeren ikke har valgt selv
    if (readThemeChoice() === 'system') applyTheme(resolveTheme('system'));
    onChange();
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_STORAGE_KEY || e.key === null) {
      applyTheme(resolveTheme(readThemeChoice()));
      onChange();
    }
  };
  listeners.add(onChange);
  mql.addEventListener('change', onSystemChange);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onChange);
    mql.removeEventListener('change', onSystemChange);
    window.removeEventListener('storage', onStorage);
  };
}
